import { ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const PRIVACY_EMAIL = "privacy@slickbytes.com";
const EFFECTIVE = "22 October 2026";

export function Privacy() {
  return (
    <div className="guide">
      <h1 className="guide-title">
        <ShieldCheck size={24} className="icon" />
        Privacy policy
      </h1>
      <p className="muted">Effective {EFFECTIVE}</p>
      <p className="lead">
        Tether is self-hosted safety software. The people who publish it do not run a server for you
        and do not receive your data. This page explains what the Tether app and gateway do with
        information, and who can see it.
      </p>

      <h2 className="guide-heading">Who is responsible</h2>
      <ul className="guide-list">
        <li>
          <strong>Your administrator</strong> is the person who set up your Tether gateway (the
          server) and its storage. Your data goes to them, and they decide who may open it. They are
          responsible for that data.
        </li>
        <li>
          <strong>The Tether project</strong> publishes the app and the software. We do not operate
          a gateway for you, we receive no location, audio or account data from the app, and we have
          no access to your recordings. Questions about this policy go to{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
        </li>
      </ul>

      <h2 className="guide-heading">What the app collects</h2>
      <ul className="guide-list">
        <li>
          <strong>Precise location</strong>, only while you have recording switched on (the Record
          or SOS buttons). This includes when the screen is off, so a person can be found in an
          emergency.
        </li>
        <li>
          <strong>Audio</strong> from the microphone, only while recording and only if audio is
          enabled in Settings.
        </li>
        <li>
          <strong>Events</strong>: when recording started and stopped, and when SOS was pressed.
        </li>
        <li>
          <strong>Device details</strong>: a random device identifier, a public key, the name you
          gave the phone, and what it may capture. These identify the phone to your gateway.
        </li>
        <li>
          <strong>A notification token</strong>, so your gateway can tell you when someone views a
          recording.
        </li>
        <li>
          <strong>The camera</strong> is used only to scan the pairing QR code. Images are not
          stored or sent.
        </li>
      </ul>
      <p>
        The app has no advertising, no analytics and no crash-reporting libraries, and it does not
        track you across other apps or websites.
      </p>

      <h2 className="guide-heading">How it is protected</h2>
      <ul className="guide-list">
        <li>
          Location, events and audio are <strong>encrypted on your phone</strong> before they leave
          it. The phone holds only the keys that lock data, never the keys that open it.
        </li>
        <li>
          The gateway and the storage behind it hold only encrypted data and cannot read it.
          Connections use HTTPS.
        </li>
        <li>
          Recordings open only through the vault&apos;s rule (for a child, the parent; for an adult,
          a trusted person or a group who must agree). Every opening is written to a log.
        </li>
        <li>
          Your administrator can see technical details such as which devices connected and when, and
          who signed in, but not the content.
        </li>
      </ul>

      <h2 className="guide-heading">Where your data goes</h2>
      <p>
        Only to the gateway address your administrator gave you, and the storage they chose (for
        example Cloudflare R2). We do not sell data or share it for advertising. Two outside
        services are involved in notifications:
      </p>
      <ul className="guide-list">
        <li>
          <strong>Expo&apos;s push notification service</strong>, and the push services run by
          Google (Android) and Apple (iPhone), deliver the &ldquo;someone viewed a recording&rdquo;
          alert. They see your notification token and the text of the alert, which names the person
          who viewed it, the time range and the reason they gave.
        </li>
        <li>
          Your administrator&apos;s hosting and storage providers handle the encrypted data and the
          connections to it.
        </li>
      </ul>

      <h2 className="guide-heading">How long it is kept, and deleting it</h2>
      <p>
        Data stays until your administrator deletes it. Nothing is deleted automatically. You can
        stop at any time: tapping Record stops sharing, and Settings → Unpair removes the
        phone&apos;s pairing and stored credentials. Your administrator can switch off a phone, and
        can delete a whole vault, which permanently erases its recordings and records from storage.
        To ask for your data to be deleted, ask your administrator. We will help you reach them if
        you are unsure who that is.
      </p>

      <h2 className="guide-heading">Why the app asks for each permission</h2>
      <ul className="guide-list">
        <li>
          <strong>Location, including &ldquo;all the time&rdquo;:</strong> to keep recording where
          you are while the screen is off.
        </li>
        <li>
          <strong>Microphone:</strong> to record audio if it is switched on.
        </li>
        <li>
          <strong>Camera:</strong> to scan the pairing code.
        </li>
        <li>
          <strong>Notifications and the always-on notice:</strong> to tell you when someone views a
          recording, and because Android requires a visible notice while an app records in the
          background.
        </li>
        <li>
          <strong>Run at startup:</strong> so background tasks and notifications keep working after
          the phone restarts.
        </li>
      </ul>

      <h2 className="guide-heading">Children</h2>
      <p>
        Tether is not directed at children. It is designed for an adult, usually a parent or
        guardian, to install on a child&apos;s phone and to hold the keys to that child&apos;s
        recordings. The phone shows a notice while it is recording, and the child&apos;s phone is
        told whenever a recording is opened.
      </p>

      <h2 className="guide-heading">The web app</h2>
      <p>
        The Tether web page uses one cookie to keep you signed in, and stores your decryption key in
        your browser. It uses no tracking or advertising cookies.
      </p>

      <h2 className="guide-heading">Changes</h2>
      <p>If this policy changes, the new version will be posted here with a new date.</p>

      <h2 className="guide-heading">Contact</h2>
      <p>
        <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>. See also the{" "}
        <Link to="/guide">guide to how Tether works</Link>.
      </p>
    </div>
  );
}
